import { useState, useRef, useEffect } from "react";
import {
  FileText,
  Upload,
  Trash2,
  Edit3,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Maximize2,
} from "lucide-react";
import apiAdmin from "../api/apiAdmin";
import AdminLayout from "../components/admin/AdminLayout";
import AdminCatalogService from "../services/admin/AdminCatalogService";
import { useNotification } from "../context/NotificationContext";
import { useAdminPermissions } from "../hooks/useAdminPermissions";
import { format } from "date-fns";

// --- Components ---

const EmptyState = ({ onUploadClick }) => (
  <div className="flex flex-col items-center justify-center min-h-[500px] p-8 bg-white dark:bg-[#1A1A1A] rounded-3xl border-2 border-dashed border-[#E5E7EB] dark:border-[#2A2A2A] transition-all duration-300 hover:border-[#62397f] group">
    <div className="w-24 h-24 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-sm">
      <FileText className="text-[#62397f] w-12 h-12" />
    </div>
    <h3 className="text-2xl font-bold text-[#111111] dark:text-white mb-3 font-poppins">
      No catalog uploaded yet
    </h3>
    <p className="text-[#666666] dark:text-[#A0A0A0] text-center max-w-md mb-8 font-inter">
      Upload a catalog (PDF File) to showcase your rental offerings. Your
      customers will be able to view and download it directly.
    </p>
    <button
      onClick={onUploadClick}
      className="flex items-center gap-2 bg-[#62397f] hover:bg-[#7C3AED] text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#62397f]/25 transition-all duration-200 active:scale-95 font-inter"
    >
      <Upload size={20} />
      Upload Catalog
    </button>
  </div>
);

const MetadataBar = ({ catalog }) => {
  const formatSize = (bytes) => {
    if (!bytes) return "Unknown size";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="bg-[#F9FAFB] dark:bg-[#222222] border border-[#E5E7EB] dark:border-[#333333] rounded-2xl p-4 mb-6 flex flex-wrap gap-6 items-center">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-white dark:bg-[#1A1A1A] rounded-lg flex items-center justify-center shadow-sm">
          <FileText className="text-red-500 w-6 h-6" />
        </div>
        <div>
          <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-medium uppercase tracking-wider font-inter">
            File Name
          </p>
          <p className="text-sm font-semibold text-[#111111] dark:text-white truncate max-w-[200px] font-inter">
            {catalog.file_name}
          </p>
        </div>
      </div>

      <div className="h-8 w-px bg-[#E5E7EB] dark:bg-[#333333] hidden sm:block" />

      <div>
        <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-medium uppercase tracking-wider font-inter">
          Type
        </p>
        <p className="text-sm font-semibold text-[#111111] dark:text-white font-inter">
          {catalog?.file_type?.toUpperCase() || "N/A"}
        </p>
      </div>

      <div className="h-8 w-px bg-[#E5E7EB] dark:bg-[#333333] hidden sm:block" />

      <div>
        <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-medium uppercase tracking-wider font-inter">
          Size
        </p>
        <p className="text-sm font-semibold text-[#111111] dark:text-white font-inter">
          {formatSize(catalog.file_size)}
        </p>
      </div>

      <div className="h-8 w-px bg-[#E5E7EB] dark:bg-[#333333] hidden sm:block" />

      <div>
        <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-medium uppercase tracking-wider font-inter">
          Last Updated
        </p>
        <p className="text-sm font-semibold text-[#111111] dark:text-white font-inter">
          {catalog.updated_at
            ? format(new Date(catalog.updated_at), "MMM d, yyyy 'at' h:mm a")
            : "Never"}
        </p>
      </div>

      {catalog.uploaded_by && (
        <>
          <div className="h-8 w-px bg-[#E5E7EB] dark:bg-[#333333] hidden lg:block" />
          <div>
            <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-medium uppercase tracking-wider font-inter">
              Uploaded By
            </p>
            <p className="text-sm font-semibold text-[#111111] dark:text-white font-inter">
              {catalog.uploaded_by}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

const CatalogViewer = ({ catalog }) => {
  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden shadow-sm h-[800px] relative">
      <iframe
        src={`${catalog.file_url}#toolbar=0`}
        className="w-full h-full border-none"
        title="Catalog PDF Viewer"
      />
      <div className="absolute bottom-6 right-6 flex gap-3">
        <a
          href={catalog.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className="p-3 bg-white/90 dark:bg-[#1A1A1A]/90 backdrop-blur-sm rounded-full border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text-white hover:bg-[#62397f] hover:text-white transition-colors shadow-lg"
          title="Open Fullscreen"
        >
          <Maximize2 size={20} />
        </a>
      </div>
    </div>
  );
};

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-[#1A1A1A] w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
          <h3 className="text-xl font-bold text-[#111111] dark:text-white font-poppins">
            {title}
          </h3>
          <button
            onClick={onClose}
            className="text-[#666666] hover:text-[#111111] dark:text-[#A0A0A0] dark:hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

// --- Page ---

export default function CatalogPage() {
  const { showNotification } = useNotification();
  const { checkPermission } = useAdminPermissions();
  const [catalog, setCatalog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);


  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const fetchCatalog = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const data = await AdminCatalogService.get();
      setCatalog(data || null);
    } catch (err) {
      console.error("Failed to fetch catalog:", err);
      setIsError(true);
      setCatalog(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);


  const removeCatalog = async () => {
    if (!checkPermission("admin", "remove the catalog")) return;
    setIsRemoving(true);
    try {
      await AdminCatalogService.delete();
      await fetchCatalog();
      setIsRemoveModalOpen(false);
      showNotification({
        type: "success",
        title: "Catalog Removed",
        message: "The catalog has been successfully removed.",
      });
    } catch (err) {
      console.error("Remove catalog failed:", err);
      showNotification({
        type: "error",
        title: "Deletion Failed",
        message: err?.response?.data?.message || "Failed to remove catalog",
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.includes("pdf")) {
        showNotification({
          type: "error",
          title: "Invalid File Type",
          message: "Only PDF files are allowed.",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleConfirmUpload = async () => {
    if (!checkPermission("admin", "upload or replace the catalog")) return;
    if (!selectedFile || isSaving) return;
    setIsSaving(true);
    try {
      await AdminCatalogService.upload(selectedFile);
      await fetchCatalog();
      setIsUploadModalOpen(false);
      setSelectedFile(null);
      showNotification({
        type: "success",
        title: catalog ? "Catalog Replaced" : "Catalog Uploaded",
        message: `Successfully ${catalog ? "replaced" : "uploaded"} the catalog PDF.`,
      });
    } catch (error) {
      console.error("Upload error:", error);
      showNotification({
        type: "error",
        title: "Upload Failed",
        message: error?.response?.data?.message || error.message || "Failed to upload file",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[600px]">
          <Loader2 className="animate-spin text-[#62397f] w-12 h-12" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#111111] dark:text-white font-poppins">
              Catalog
            </h1>
            <p className="text-[#666666] dark:text-[#A0A0A0] font-inter mt-1">
              {catalog ? "Your current rental catalog" : "No catalog uploaded"}
            </p>
          </div>
          {catalog && (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsUploadModalOpen(true)}
                className="flex items-center gap-2 bg-[#F5F3FF] dark:bg-[#2A2440] text-[#62397f] px-5 py-2.5 rounded-xl font-semibold border border-[#62397f]/20 hover:bg-[#62397f] hover:text-white transition-all duration-200 active:scale-95 font-inter"
              >
                <Edit3 size={18} />
                Replace Catalog
              </button>
              {/* 
              <button
                onClick={() => setIsRemoveModalOpen(true)}
                className="flex items-center gap-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 active:scale-95 font-inter"
              >
                <Trash2 size={18} />
                Remove
              </button>
              */}
            </div>
          )}
        </div>

        {/* Content Section */}
        {!catalog || !catalog.file_url ? (
          <EmptyState onUploadClick={() => setIsUploadModalOpen(true)} />
        ) : (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <MetadataBar catalog={catalog} />
            <CatalogViewer catalog={catalog} />
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={isUploadModalOpen}
        onClose={() => {
          if (!isSaving) {
            setIsUploadModalOpen(false);
            setSelectedFile(null);
          }
        }}
        title={catalog ? "Replace Catalog" : "Upload Catalog"}
      >
        <div className="space-y-6">
          {catalog && (
            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/50 p-4 rounded-2xl flex gap-3">
              <AlertCircle className="text-amber-500 shrink-0" size={20} />
              <p className="text-sm text-amber-800 dark:text-amber-200 font-inter">
                Replacing the catalog will overwrite the current one. This
                action cannot be undone.
              </p>
            </div>
          )}

          {!selectedFile ? (
            <div
              onClick={() => fileInputRef.current.click()}
              className="border-2 border-dashed border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl p-10 flex flex-col items-center justify-center cursor-pointer hover:border-[#62397f] hover:bg-[#F5F3FF]/30 dark:hover:bg-[#2A2440]/30 transition-all group"
            >
              <div className="w-16 h-16 bg-[#F5F3FF] dark:bg-[#2A2440] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Upload className="text-[#62397f]" size={32} />
              </div>
              <p className="font-semibold text-[#111111] dark:text-white font-inter">
                Click to upload or drag and drop
              </p>
              <p className="text-sm text-[#666666] dark:text-[#A0A0A0] mt-1 font-inter">
                PDF format only (max. 10MB)
              </p>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileChange}
                accept=".pdf"
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-[#F9FAFB] dark:bg-[#222222] p-4 rounded-2xl border border-[#E5E7EB] dark:border-[#333333]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white dark:bg-[#1A1A1A] rounded-lg flex items-center justify-center shadow-sm">
                    <FileText className="text-red-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#111111] dark:text-white truncate max-w-[200px] font-inter">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-[#666666] dark:text-[#A0A0A0] font-inter">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                  }}
                  className="p-2 text-[#666666] hover:text-red-500 transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => {
                setIsUploadModalOpen(false);
                setSelectedFile(null);
              }}
              disabled={isSaving}
              className="flex-1 px-6 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#333333] text-[#666666] dark:text-[#A0A0A0] font-semibold hover:bg-[#F9FAFB] dark:hover:bg-[#222222] transition-colors disabled:opacity-50 font-inter"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmUpload}
              disabled={
                !selectedFile || isSaving
              }
              className="flex-1 px-6 py-3 rounded-xl bg-[#62397f] text-white font-semibold hover:bg-[#7C3AED] transition-colors shadow-lg shadow-[#62397f]/25 flex items-center justify-center gap-2 disabled:opacity-50 font-inter"
            >
              {isSaving ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Uploading...
                </>
              ) : (
                <>
                  <CheckCircle2 size={18} />
                  Confirm {catalog ? "Replace" : "Upload"}
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Remove Modal */}
      <Modal
        isOpen={isRemoveModalOpen}
        onClose={() => !isRemoving && setIsRemoveModalOpen(false)}
        title="Remove Catalog"
      >
        <div className="space-y-6">
          <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 p-4 rounded-2xl flex gap-3">
            <AlertCircle className="text-red-500 shrink-0" size={20} />
            <p className="text-sm text-red-800 dark:text-red-200 font-inter">
              This will remove the current catalog. You can upload a new one at
              any time. This action is irreversible.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setIsRemoveModalOpen(false)}
              disabled={isRemoving}
              className="flex-1 px-6 py-3 rounded-xl border border-[#E5E7EB] dark:border-[#333333] text-[#666666] dark:text-[#A0A0A0] font-semibold hover:bg-[#F9FAFB] dark:hover:bg-[#222222] transition-colors disabled:opacity-50 font-inter"
            >
              Cancel
            </button>
            <button
              onClick={removeCatalog}
              disabled={isRemoving}
              className="flex-1 px-6 py-3 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors shadow-lg shadow-red-500/25 flex items-center justify-center gap-2 disabled:opacity-50 font-inter"
            >
              {isRemoving ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Trash2 size={18} />
              )}
              Remove Catalog
            </button>
          </div>
        </div>
      </Modal>

      <style jsx="true" global="true">{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
        .animate-slide-up {
          animation: slideInUp 0.5s ease-out forwards;
        }
      `}</style>
    </AdminLayout>
  );
}
